const RETRYABLE_HTTP_STATUSES = new Set([502, 503, 504]);
const DEFAULT_RETRY_DELAY_MS = 300;

function createAbortError() {
  const error = new Error('The request was aborted.');
  error.name = 'AbortError';
  return error;
}

function waitForRetry(delayMs, signal) {
  if (signal && signal.aborted) {
    return Promise.reject(createAbortError());
  }

  return new Promise((resolve, reject) => {
    let timeoutId;

    const handleAbort = () => {
      clearTimeout(timeoutId);
      reject(createAbortError());
    };

    timeoutId = setTimeout(() => {
      if (signal) signal.removeEventListener('abort', handleAbort);
      resolve();
    }, delayMs);

    if (signal) signal.addEventListener('abort', handleAbort, { once: true });
  });
}

function createHttpError(response) {
  const error = new Error(`GraphQL request failed with HTTP status ${response.status}`);
  error.name = 'GraphQLHttpError';
  error.status = response.status;
  error.response = response;
  return error;
}

function isSuccessfulResponse(response) {
  return response.status >= 200 && response.status < 300;
}

export default async function fetchGraphQLWithRetry({
  fetchFn = fetch,
  operation,
  url,
  requestInit,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS,
}) {
  const canRetry = operation.operationKind === 'query';
  const maxAttempts = canRetry ? 2 : 1;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let response;

    try {
      response = await fetchFn(url, requestInit);
    } catch (error) {
      const shouldRetryNetworkFailure =
        attempt < maxAttempts &&
        error.name !== 'AbortError' &&
        !(requestInit.signal && requestInit.signal.aborted);

      if (!shouldRetryNetworkFailure) throw error;

      await waitForRetry(retryDelayMs, requestInit.signal);
      continue;
    }

    if (RETRYABLE_HTTP_STATUSES.has(response.status) && attempt < maxAttempts) {
      await waitForRetry(retryDelayMs, requestInit.signal);
      continue;
    }

    if (!isSuccessfulResponse(response)) throw createHttpError(response);

    // GraphQL payloads containing `errors` are valid transport responses and
    // must be returned to Relay without triggering transport-level retries.
    return response.json();
  }

  throw new Error('GraphQL request failed without a response.');
}

export { DEFAULT_RETRY_DELAY_MS, RETRYABLE_HTTP_STATUSES };
